import React, { useEffect, useRef, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { DOCUMENTO_DESIGN as D } from '../EditorDocumento.design';

function formatHora(data) {
  try {
    return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(data));
  } catch {
    return '';
  }
}

// Painel inline (não é modal) do agente de IA na barra lateral: histórico de
// mensagens persistido em `proposta.mensagens_ia` (ou `template.mensagens_ia`, no
// editor de modelos) + composer para enviar novas instruções. `onEnviar(instrucao)`
// deve retornar uma Promise (o próprio chamador já cuida de anexar a mensagem do
// usuário/IA ao histórico). `mensagemVazia`/`placeholder` permitem adaptar os textos
// onde não existe vínculo com conversa/oportunidade (caso do editor de modelos).
export default function AgenteIaPainel({ mensagens, readOnly, chatVinculado, selectedLead, onEnviar, autoFocus, mensagemVazia, placeholder }) {
  const [instrucao, setInstrucao] = useState('');
  const [loading, setLoading] = useState(false);
  const [avisoLocal, setAvisoLocal] = useState('');
  const listRef = useRef(null);
  const textareaRef = useRef(null);

  const lista = mensagens || [];
  const temContexto = Boolean(chatVinculado || selectedLead);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [lista.length, loading]);

  // Atalho "Criar com IA" da listagem: chega aqui já pronto pra digitar.
  useEffect(() => {
    if (autoFocus && !readOnly) textareaRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnviar = async () => {
    const texto = instrucao.trim();
    if (!texto && !temContexto) {
      setAvisoLocal(mensagemVazia
        ? 'Descreva o que o agente deve montar.'
        : 'Descreva o que deseja incluir na proposta ou vincule uma conversa/oportunidade.');
      return;
    }
    setAvisoLocal('');
    setLoading(true);
    setInstrucao('');
    try {
      await onEnviar(texto);
    } catch {
      // o erro já fica registrado no histórico (mensagens_ia) pelo chamador
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Área de mensagens: cresce para ocupar o espaço disponível na sidebar
          (em vez de uma altura fixa pequena), com scroll interno próprio.
          justify-end ancora as mensagens perto do campo de digitação quando há
          poucas (como WhatsApp/iMessage) — o vazio, se houver, fica no topo. */}
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-end space-y-2 mb-2.5 pr-0.5">
        {lista.length === 0 ? (
          <div className={`flex items-start gap-2 p-2.5 rounded-xl text-[11px] ${temContexto || mensagemVazia ? 'bg-violet-50/60 dark:bg-violet-500/5 text-gray-600 dark:text-[#ABABAD]' : 'bg-amber-50 dark:bg-amber-500/5 text-amber-700 dark:text-amber-400'}`}>
            <Iconify icon={temContexto || mensagemVazia ? 'mdi:information-outline' : 'mdi:alert-outline'} width={13} className="flex-shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              {chatVinculado && selectedLead && `Vou usar a conversa vinculada e os dados de "${selectedLead.nome}" como contexto.`}
              {chatVinculado && !selectedLead && 'Vou usar a conversa do WhatsApp vinculada como contexto.'}
              {!chatVinculado && selectedLead && `Vou usar os dados da oportunidade "${selectedLead.nome}" como contexto.`}
              {!temContexto && (mensagemVazia || 'Nenhuma conversa/oportunidade vinculada — descreva abaixo o que deve entrar na proposta.')}
            </p>
          </div>
        ) : (
          lista.map((m, i) => (
            <div
              key={i}
              className={`text-[13px] rounded-xl px-3 py-2 leading-relaxed ${
                m.role === 'user'
                  ? 'bg-[#F1EAFE] dark:bg-violet-500/10 text-gray-800 dark:text-[#D1D2D3] ml-4'
                  : m.erro
                    ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 mr-4'
                    : 'bg-[#F6F5FA] dark:bg-[#1A1D21] text-gray-700 dark:text-[#ABABAD] mr-4'
              }`}
            >
              <p>{m.texto}</p>
              {m.data && <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-1">{formatHora(m.data)}</p>}
            </div>
          ))
        )}
        {loading && (
          <div className="text-xs rounded-xl px-3 py-2 bg-gray-50 dark:bg-[#1A1D21] text-gray-400 mr-4 flex items-center gap-1.5">
            <Iconify icon="mdi:loading" className="animate-spin" width={12} />
            Gerando...
          </div>
        )}
      </div>

      {!readOnly && (
        <div className="flex-shrink-0">
          {avisoLocal && <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-1.5">{avisoLocal}</p>}
          <div className="flex items-end gap-1.5">
            <textarea
              ref={textareaRef}
              value={instrucao}
              onChange={e => setInstrucao(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || 'Descreva o que deseja incluir...'}
              rows={2}
              disabled={loading}
              className={`${D.textarea} flex-1 !min-h-0 text-[13px] resize-none`}
            />
            <button
              type="button"
              onClick={handleEnviar}
              disabled={loading}
              title="Enviar"
              className={`${D.btn} ${D.btnPrimary} !px-0 w-[38px] flex-shrink-0`}
            >
              <Iconify icon={loading ? 'mdi:loading' : 'mdi:send'} className={loading ? 'animate-spin' : ''} width={14} />
            </button>
          </div>
          {lista.length === 0 && (
            <p className="text-[10px] text-gray-400 mt-1.5 leading-relaxed">
              A IA pode errar itens e valores — revise tudo antes de {mensagemVazia ? 'usar o modelo' : 'enviar a proposta ao cliente'}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
